/**
 * Jovii — PLAYER, ONE idle timer for everything drawn over the film.
 *
 * ── WHY THIS IS A MODULE AND NOT A LOCAL VARIABLE ──────────────────────────────
 *
 * The player's own chrome — top bar, control bar, play/pause, ±10 s, subtitles —
 * fades after a few seconds and comes back on a tap. The call window's controls did
 * not: they sat over the film for the whole evening. The obvious fix is a second
 * timer inside `CallDock`, and it is the wrong one. Two timers started at different
 * moments drift apart within seconds, and then a tap brings back one set of buttons
 * and not the other, or half the screen fades while the other half does not. The
 * owner asked for exactly one behaviour, so there is exactly one timer and exactly
 * one flag, and both components read it.
 *
 * The call dock cannot simply be handed a prop, either: it is mounted from the
 * catalogue's `overlay` snippet (`routes/Watch.svelte`), portalled to `<body>`, and
 * the player has no reference to it. A module singleton is what they can both see.
 *
 * ── WHO MAY DO WHAT ────────────────────────────────────────────────────────────
 *
 * ONE OWNER OF THE FLAG, and it is this object. Nothing else writes `visible`;
 * components subscribe and mirror it. (Two owners for one number is how this
 * codebase got a 268 px offset and a 91 px overflow — see `calls/CallDock.svelte`.)
 *
 *  - `arm(canHide)` — the PLAYER's condition, and only the player's: the film is
 *    actually playing, no sheet is open, no notice is up. Nothing fades otherwise.
 *  - `poke()` — anybody. A pointer moved, a key was pressed, the dock was touched.
 *  - `hide()` — the touch toggle: on a phone a tap on the picture hides the chrome
 *    rather than pausing.
 *  - `hold()` — while a gesture owns the screen (dragging the call window), nothing
 *    may fade underneath it. Returns its own release, and holds are counted, so two
 *    holders cannot cancel each other.
 *
 * There is no `show()`. Everything that wants the chrome back also wants the idle
 * countdown restarted, which is what `poke` is.
 *
 * ── AND ONE MORE FACT ABOUT WHAT IS OVER THE FILM ──────────────────────────────
 *
 * `setModal(open)` says whether one of the player's sheets — sources, audio,
 * subtitles, details, episodes — is up. It lives here for the same reason the
 * flag above does: the sheet belongs to the player and the thing that has to
 * react to it is the CALL WINDOW, which the player has no reference to.
 *
 * The call dock is portalled to `<body>` at the top of the z-order, deliberately,
 * so that it owns its own hit test and cannot be buried by anything drawn over
 * the film. That is right for a film and wrong for a sheet: measured, opening the
 * episode sheet while on a call left the episode rows under the call window
 * unpressable — a driven browser reported the dock's own <video> intercepting the
 * click. The dock now steps aside while a sheet is up, which is the only place
 * either component has to know about the other.
 */

/** How long the chrome stays up with nothing happening. */
export const CHROME_IDLE_MS = 3200;

export type ChromeListener = (visible: boolean) => void;

export interface ChromeOptions {
  idleMs?: number;
  /** Injected by the tests. Returns a handle for `clear`. */
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export class ChromeVisibility {
  private shown = true;
  /** One of the player's sheets is open over the film. */
  private modalOpen = false;
  private readonly modalListeners = new Set<(open: boolean) => void>();
  /** Idle hiding applies at all — i.e. a film is playing and nothing is on top. */
  private armed = false;
  private holds = 0;
  private handle: unknown = null;

  private readonly idleMs: number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly listeners = new Set<ChromeListener>();

  constructor(opts: ChromeOptions = {}) {
    this.idleMs = opts.idleMs ?? CHROME_IDLE_MS;
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  get visible(): boolean {
    return this.shown;
  }

  /** Mostly for the tests and for anyone debugging why nothing is fading. */
  get idleHiding(): boolean {
    return this.armed && this.holds === 0;
  }

  /**
   * Svelte store contract, on purpose: `$effect(() => chrome.subscribe(...))` in a
   * component both subscribes and unsubscribes with one line, and the immediate
   * call means a component that mounts mid-film starts in the right state instead
   * of flashing its controls on.
   */
  subscribe(fn: ChromeListener): () => void {
    this.listeners.add(fn);
    fn(this.shown);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * The player's condition for fading at all.
   *
   * Idempotent by design: it is called from an effect that re-runs on unrelated
   * state, and re-arming on every re-run would silently extend the countdown for
   * as long as anything on the screen kept changing.
   */
  arm(canHide: boolean): void {
    if (canHide === this.armed) return;
    this.armed = canHide;
    if (!canHide) {
      this.stop();
      this.set(true);
      return;
    }
    this.schedule();
  }

  /** Something happened. Show the chrome and start the countdown again. */
  poke(): void {
    this.set(true);
    this.schedule();
  }

  /** The touch toggle's other half. A held gesture is never hidden underneath. */
  hide(): void {
    if (this.holds > 0) return;
    this.stop();
    this.set(false);
  }

  toggle(): void {
    if (this.shown) this.hide();
    else this.poke();
  }

  /**
   * Keep the chrome up until the returned function is called — a drag in progress,
   * mainly. Counted, and each release fires once, so an effect cleanup that runs
   * twice cannot leave the count below zero.
   */
  hold(): () => void {
    this.holds += 1;
    this.stop();
    this.set(true);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds = Math.max(0, this.holds - 1);
      this.schedule();
    };
  }

  /**
   * A sheet is open over the film, or is not. The PLAYER owns this — it is the
   * only thing that has sheets — and the call dock reads it.
   */
  setModal(open: boolean): void {
    if (open === this.modalOpen) return;
    this.modalOpen = open;
    for (const fn of this.modalListeners) fn(open);
  }

  get modal(): boolean {
    return this.modalOpen;
  }

  /** Subscribe to sheet-open. Fires immediately with the current value, like
   *  `subscribe`, so a dock mounted mid-sheet is not left in the wrong state. */
  onModal(fn: (open: boolean) => void): () => void {
    this.modalListeners.add(fn);
    fn(this.modalOpen);
    return () => this.modalListeners.delete(fn);
  }

  /** Leaving the player. Anything still mounted (the dock is) must not be left
   *  faded out with no timer alive to bring it back — nor stood aside for a sheet
   *  that was destroyed along with the player. */
  reset(): void {
    this.stop();
    this.armed = false;
    this.holds = 0;
    this.set(true);
    this.setModal(false);
  }

  private schedule(): void {
    this.stop();
    if (!this.armed || this.holds > 0) return;
    this.handle = this.setTimer(() => {
      this.handle = null;
      if (!this.armed || this.holds > 0) return;
      this.set(false);
    }, this.idleMs);
  }

  private stop(): void {
    if (this.handle === null) return;
    this.clearTimer(this.handle);
    this.handle = null;
  }

  private set(next: boolean): void {
    if (next === this.shown) return;
    this.shown = next;
    for (const fn of this.listeners) fn(next);
  }
}

/**
 * The tab's chrome state. The player arms it; the player and the call dock both
 * read it and both poke it.
 */
export const chrome = new ChromeVisibility();
